// src/data/questions/breachQuestions.ts
// Breach Preparedness Questions (Q33-Q36)

import { Question } from '../../types';

export const breachQuestions: Question[] = [
  {
    id: 'Q33',
    section: 'breach',
    question: 'Have you experienced a breach or security incident in the last 24 months?',
    type: 'single',
    options: [
      { value: 'yes_reported', label: 'Yes, and we followed breach notification procedures', description: '', points: 3 },
      { value: 'yes_unreported', label: 'Yes, but we didn\'t report it (didn\'t meet threshold)', description: '', points: 1 },
      { value: 'no', label: 'No breaches', description: '', points: 3 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.400-414 - Breach Notification Rule',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "This question establishes breach history for context. A breach is unauthorized access/acquisition/use/disclosure of PHI that compromises the privacy or security of the information. Not all security incidents are breaches; a breach requires actual or probable disclosure to unauthorized third parties. Many incidents are contained quickly and no breach occurs. If you've had breaches, you must have followed breach notification procedures: risk assessment (was it really a breach?), notification to affected individuals within 60 days, notification to media (if 500+ individuals), notification to HHS. You must document all of this.",
      hipaaReference: "§164.400-414 - Breach Notification Rule. Status: Required if breach occurs. Effective Date: HIPAA (1996); Breach Notification Rule (2009). Organizations with recent breaches face more aggressive OCR enforcement. Repeat breaches (multiple in 5 years) show organizational problems and trigger larger penalties. Breach documentation is required; inability to produce records = separate violation. Insurance companies review breach history; multiple breaches increase premiums or cause cancellation.",
      realWorldExample: "A clinic experiences a ransomware attack. Attackers encrypt all files and demand payment. The clinic pays the ransom. Later investigation shows the attackers accessed 5 years of patient records. Even though clinic paid ransom, this is a breach; 6 years of backups potentially exposed. Clinic must notify 50,000 affected individuals within 60 days. A hospital discovers an employee has been accessing celebrity patients' records without authorization (snooping). Investigation shows access to 30 records over 6 months. This is a breach of privacy (unauthorized access + disclosure within organization). Hospital notifies 30 individuals. A clinic updates their EHR software; a misconfiguration briefly exposes patient records on the internet. Clinic discovers this within 2 hours, patches the configuration, and verifies no access logs show unauthorized access. This is a security incident but not a breach (no unauthorized access). No notification needed.",
      estimatedCost: {
        small: "Incident response plan: $3,000-7,000",
        medium: "Risk assessment process: $2,000-5,000 (training)",
        large: "Breach notification: $0-10,000 per breach (depends on number of affected individuals and notification method)"
      },
      implementationSteps: [
        "Risk assessment: 1-2 weeks per incident",
        "Notification: must complete within 60 days",
        "Investigation: 2-12 weeks"
      ],
      commonMistakes: [
        "Not conducting proper risk assessment to determine if incident is a breach",
        "Missing the 60-day notification deadline",
        "Inadequate documentation of breach response procedures",
        "Not notifying all required parties (individuals, media, HHS)"
      ],
      resourceLinks: [
        { title: "HHS Breach Notification Rule", url: "https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "1-2 weeks per incident",
        medium: "2-4 weeks per incident",
        large: "4-12 weeks per incident"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Security Manager",
        "Compliance Officer",
        "Risk Management",
        "Legal Counsel"
      ],
      whereToLook: [
        "Incident response logs",
        "Security incident reports",
        "Breach notification records",
        "OCR correspondence"
      ],
      howToVerify: "Review incident logs and security reports from the last 24 months. Check for any unauthorized access, data theft, ransomware, or other security events. Verify if proper breach notification procedures were followed.",
      whatToDocument: "Number and types of security incidents, breach determinations made, notification timelines followed, documentation of risk assessments"
    }
  },
  {
    id: 'Q34',
    section: 'breach',
    question: 'Do you have a documented breach response plan?',
    type: 'single',
    options: [
      { value: 'detailed', label: 'Yes, detailed plan with notification templates and timelines', description: '', points: 3 },
      { value: 'basic', label: 'Basic plan', description: '', points: 2 },
      { value: 'no', label: 'No formal breach response plan', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.404 - Notification timeline (60 days)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "A breach response plan is a detailed procedure for handling breaches from detection through resolution. It includes: (1) detection procedures (how you discover a breach), (2) investigation steps (what to do first), (3) notification procedures (who to contact, what to say), (4) timeline requirements (60-day notification deadline), (5) evidence preservation, and (6) post-incident review. The plan should be detailed and specific to your organization, with assigned roles, contact lists, and templates. Many organizations have incident response plans but not breach response specifically. Breaches have unique requirements (regulatory notification, legal holds, specific timelines) that differ from general security incidents.",
      hipaaReference: "§164.404 - Notification Requirement (specifies 60-day timeline); requires breach response procedures. Status: Required. Effective Date: Breach Notification Rule (2009). Without a documented plan, breach response is chaotic and errors are common. Missing the 60-day notification deadline = automatic OCR violation with separate fine. A detailed plan provides legal defensibility (shows you responded properly). Insurance coverage often requires documented plan as condition of coverage.",
      realWorldExample: "A clinic with a detailed breach response plan discovers a breach. Within 1 hour, incident response team is assembled. Within 4 hours, investigation begins. Within 2 weeks, investigation is complete; 200 affected individuals identified. Within 60 days, all notifications sent. Documentation is complete. OCR audit finds no issues. A clinic without a plan discovers a breach. They panic, don't know who to contact, spend 2 weeks just figuring out the scope of breach, miss the 60-day notification deadline by 5 days. OCR fines them for late notification AND cites them for lack of response plan.",
      estimatedCost: {
        small: "Breach response plan development: $3,000-7,000 (consultant-assisted)",
        medium: "Breach notification vendor (retainer): $5,000-20,000/year",
        large: "Legal counsel (if needed for specific breach): $5,000-20,000+ (hourly billing)"
      },
      implementationSteps: [
        "Plan development: 3-4 weeks",
        "Team training/tabletop exercise: 1-2 weeks",
        "Annual testing and updates: 1 week/year"
      ],
      commonMistakes: [
        "Having incident response plan but not breach-specific procedures",
        "Not including specific notification templates and timelines",
        "Failing to assign specific roles and responsibilities",
        "Not testing the plan with tabletop exercises"
      ],
      resourceLinks: [
        { title: "HHS Breach Notification Rule", url: "https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "3-4 weeks",
        medium: "4-6 weeks",
        large: "6-8 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Compliance Officer",
        "IT Security Manager",
        "Legal Counsel",
        "Risk Management"
      ],
      whereToLook: [
        "Incident response documentation",
        "Breach response procedures",
        "Notification templates",
        "Contact lists and escalation procedures"
      ],
      howToVerify: "Request to see the written breach response plan. Check if it includes specific procedures, timelines, roles, and notification templates. Verify if staff have been trained on the plan.",
      whatToDocument: "Existence of documented plan, level of detail, inclusion of notification procedures, staff training records, plan testing/exercise records"
    }
  },
  {
    id: 'Q35',
    section: 'breach',
    question: 'Do you conduct breach risk assessments when incidents occur?',
    type: 'single',
    options: [
      { value: 'formal', label: 'Yes, formal 4-factor risk assessment (NIST framework)', description: '', points: 3 },
      { value: 'informal', label: 'Informal assessment', description: '', points: 1 },
      { value: 'no', label: 'No risk assessment process', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.402 - Breach definition and risk assessment',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "A breach risk assessment determines whether a security incident actually constitutes a \"breach\" under HIPAA. Not all security incidents are breaches. HIPAA defines a breach as unauthorized access/acquisition/use/disclosure of PHI that compromises the privacy or security of the information. A risk assessment uses a 4-factor test to determine if a breach occurred. The NIST framework uses: (1) nature and extent of PHI involved, (2) who accessed/acquired PHI and what they likely do with it, (3) extent to which risk is mitigated (e.g., PHI was encrypted, so unlikely to be useful), and (4) evidence of actual unauthorized access or misuse. If you can demonstrate low risk (e.g., encrypted data that was never decrypted, data accessed by authorized person but without clear evidence of misuse), you may determine a breach did not occur and no notification is needed.",
      hipaaReference: "§164.402 - Breach Definition and Risk Assessment requirement. Status: Required (when incident occurs). Effective Date: Breach Notification Rule (2009). Risk assessment determines whether you notify individuals (or not). If you conduct a risk assessment and reasonably determine no breach occurred, you have legal defensibility. Failure to conduct assessment and defaulting to \"notify everyone\" can cause unnecessary panic and negative publicity. OCR reviews risk assessments during investigations; poorly conducted assessments are cited as violations.",
      realWorldExample: "A clinic discovers an employee accessed a patient's record without authorization (snooping). Risk assessment: PHI accessed (yes); disclosed to others (no evidence found); patient already shared this information on social media (limited sensitivity); encryption in place (N/A; was internal access). Conclusion: privacy breach occurred (unauthorized access), no harm breach (no disclosure/misuse). Employee is disciplined; patient is notified of the privacy breach as a courtesy, but not a \"harm breach.\" A hospital discovers a backup drive containing encrypted PHI was lost in transit. Risk assessment: PHI type (clinical data, some sensitive); encryption (AES-256, strong); vendor had no key to decrypt (encryption keys held separately). Conclusion: security incident but low risk breach (encryption provides reasonable assurance data isn't accessible). Hospital notifies individuals as a precaution but explains encryption mitigates risk. No regulatory fine for breach notification delay because assessment supports determination of low-risk incident.",
      estimatedCost: {
        small: "Risk assessment training/framework: $1,000-3,000",
        medium: "Per-incident assessment (internal): 10-20 hours (~$1,000-2,000 staff time)",
        large: "Forensic investigation (if needed): $5,000-30,000+"
      },
      implementationSteps: [
        "Establish assessment framework: 1-2 weeks",
        "Per-incident assessment: 1-2 weeks (simple) to 4-8 weeks (complex with forensics)"
      ],
      commonMistakes: [
        "Not conducting formal risk assessment for every incident",
        "Inadequate documentation of assessment reasoning",
        "Not using the NIST 4-factor framework consistently",
        "Defaulting to breach notification without proper assessment"
      ],
      resourceLinks: [
        { title: "HHS Breach Notification Rule", url: "https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "1-2 weeks per incident",
        medium: "2-4 weeks per incident",
        large: "4-8 weeks per incident"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Compliance Officer",
        "IT Security Manager",
        "Risk Management",
        "Legal Counsel"
      ],
      whereToLook: [
        "Risk assessment documentation",
        "Incident investigation reports",
        "NIST framework implementation",
        "Assessment methodology procedures"
      ],
      howToVerify: "Request examples of risk assessments conducted for past incidents. Check if they follow the NIST 4-factor framework and are properly documented. Verify staff are trained on assessment procedures.",
      whatToDocument: "Risk assessment methodology used, documentation of assessments conducted, training records for assessment procedures, consistency of assessment application"
    }
  },
  {
    id: 'Q36',
    section: 'breach',
    question: 'Did you notify affected individuals within 60 days?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes, within 60 days', description: '', points: 3 },
      { value: 'late', label: 'No, took longer than 60 days', description: '', points: 0 },
      { value: 'nonotify', label: 'We didn\'t notify individuals', description: '', points: 0 },
      { value: 'na', label: 'Not applicable - no breach', description: '', points: 3 },
    ],
    regulation: '§164.404(b) - Timeliness of notification (60 days REQUIRED)',
    required: false,
    conditionalOn: { questionId: 'Q33', requiredValue: ['yes_reported', 'yes_unreported'] },
    weight: 3,
    tooltipContent: {
      plainEnglish: "The Breach Notification Rule requires notification of affected individuals within 60 days of discovery of a breach. This means: (1) you discover the breach (60-day clock starts), (2) you conduct risk assessment to confirm it's a breach, (3) you prepare notification, (4) you deliver notification. All within 60 days. The 60 days is strict. If you miss it by even 1 day, OCR can fine you. Many organizations underestimate the time needed to notify large numbers of individuals. Notifications must be: (1) written (email, letter, or secure portal message), (2) include specific information (what happened, what data was involved, what you're doing about it, what individuals should do), and (3) delivered to each affected individual.",
      hipaaReference: "§164.404(b) - Timeliness of notification (60 days REQUIRED; without unreasonable delay). Status: Required (strictly enforced). Effective Date: Breach Notification Rule (2009). Missing the 60-day deadline is a separate, automatic OCR violation with fines. Even if breach was not your fault, 60-day deadline still applies. Notifying 10,000+ individuals in 60 days requires serious planning (vendor support, template preparation). Late notification damages trust and invites state AG investigations.",
      realWorldExample: "A clinic discovers a breach on January 1. By February 15 (45 days), they've completed investigation, prepared notifications, and sent them to all 500 affected individuals. Compliant. A hospital discovers a breach on January 1. Their investigation is thorough but takes 30 days. They prepare notifications, but outsource mailing to a vendor who takes 15 days to process. By day 50, notifications are in the mail. By day 58, all notifications have been received. Compliant (tight, but made the deadline). A clinic discovers a breach on January 1 but doesn't start investigation until January 15 (14-day delay). Investigation takes 30 days. Notifications are prepared but mailing takes 20 days. By day 59, the last notification is sent. Compliant (just barely). Another clinic discovers a breach on January 1. They investigate (30 days). Then they wait for board approval before notifying (another 20 days). By day 52, they send notifications. For a large breach with 1,000+ individuals and mailing delays, they miss the deadline. OCR fine applies.",
      estimatedCost: {
        small: "Notification preparation: $2,000-5,000 (templates, legal review)",
        medium: "Breach notification vendor (for large breaches): $5,000-20,000+",
        large: "Credit monitoring (often offered to affected individuals): $50,000-200,000+ (for large breaches)"
      },
      implementationSteps: [
        "Notification decision/preparation: 1-2 weeks",
        "Notification delivery: must complete within 60 days",
        "Credit monitoring setup (if offered): 1-2 weeks"
      ],
      commonMistakes: [
        "Underestimating time needed for large-scale notifications",
        "Not having pre-approved notification templates ready",
        "Delays in investigation that consume too much of the 60-day window",
        "Not accounting for mailing/delivery time in the 60-day calculation"
      ],
      resourceLinks: [
        { title: "HHS Breach Notification Rule", url: "https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html", type: "external" }
      ],
            timeToComplete: {
        small: "Must complete within 60 days",
        medium: "Must complete within 60 days",
        large: "Must complete within 60 days"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Compliance Officer",
        "Legal Counsel",
        "Communications Manager",
        "Breach Response Team"
      ],
      whereToLook: [
        "Breach notification records",
        "Notification delivery confirmations",
        "Timeline documentation",
        "Vendor contracts for notification services"
      ],
      howToVerify: "Review breach notification records to verify timing. Check delivery confirmations and documentation showing all affected individuals were notified within the 60-day deadline.",
      whatToDocument: "Notification timeline, delivery method used, number of individuals notified, delivery confirmations, any delays encountered and how they were addressed"
    }
  },
];
